'use client'

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdArrowBack,
    MdDelete,
    MdEdit,
    MdEmail,
} from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import { ModalContainer, ModalActions } from '@/components/container/container'
import { BackdropLoading } from '@/components/backdrop-loading'
import RichTextEditor from '@/components/rich-text-editor'
import '@/components/rich-text-editor/styles.css'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import type { EmailTemplate } from '@/lib/api/administrative/inbox-api'
import {
    getEmailTemplates,
    createEmailTemplate,
    updateEmailTemplate,
    deleteEmailTemplate,
} from '@/lib/api/administrative/inbox-api'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

export default function InboxTemplatesPage() {
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'administrative',
        'inbox',
    )

    const [templates, setTemplates] = useState<EmailTemplate[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)

    // Form
    const [showForm, setShowForm] = useState(false)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [formName, setFormName] = useState('')
    const [formSubject, setFormSubject] = useState('')
    const [formBodyHtml, setFormBodyHtml] = useState('')

    // Delete
    const [deleteModalId, setDeleteModalId] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        loadTemplates()
    }, [])

    const loadTemplates = async () => {
        setLoading(true)
        try {
            const response = await getEmailTemplates()
            if (response.success) {
                setTemplates(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar modelos')
            }
        } catch {
            toast.error('Erro ao carregar modelos')
        } finally {
            setLoading(false)
        }
    }

    const openNewForm = () => {
        setEditingId(null)
        setFormName('')
        setFormSubject('')
        setFormBodyHtml('')
        setShowForm(true)
    }

    const openEditForm = (tpl: EmailTemplate) => {
        setEditingId(tpl.id)
        setFormName(tpl.name)
        setFormSubject(tpl.subject || '')
        setFormBodyHtml(tpl.bodyHtml)
        setShowForm(true)
    }

    const closeForm = () => {
        setShowForm(false)
        setEditingId(null)
    }

    const handleSave = async () => {
        if (!formName.trim()) {
            toast.warning('Informe o nome do modelo')
            return
        }
        if (!formBodyHtml.trim()) {
            toast.warning('Informe o conteúdo do modelo')
            return
        }

        setSaving(true)
        try {
            if (editingId) {
                const response = await updateEmailTemplate(editingId, {
                    name: formName,
                    subject: formSubject || undefined,
                    bodyHtml: formBodyHtml,
                })
                if (response.success) {
                    toast.success('Modelo atualizado')
                    closeForm()
                    loadTemplates()
                } else {
                    toast.error(
                        response.error || 'Erro ao atualizar modelo',
                    )
                }
            } else {
                const response = await createEmailTemplate({
                    name: formName,
                    subject: formSubject || undefined,
                    bodyHtml: formBodyHtml,
                })
                if (response.success) {
                    toast.success('Modelo criado')
                    closeForm()
                    loadTemplates()
                } else {
                    toast.error(response.error || 'Erro ao criar modelo')
                }
            }
        } catch {
            toast.error('Erro ao salvar modelo')
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async () => {
        if (!deleteModalId) return
        setDeleting(true)
        try {
            const response = await deleteEmailTemplate(deleteModalId)
            if (response.success) {
                toast.success('Modelo excluído')
                loadTemplates()
            } else {
                toast.error(response.error || 'Erro ao excluir modelo')
            }
        } catch {
            toast.error('Erro ao excluir modelo')
        } finally {
            setDeleting(false)
            setDeleteModalId(null)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando modelos de email...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => navigate('/administrative/inbox')}
                    >
                        <MdArrowBack className="text-lg" />
                    </button>
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-secondary via-secondary/90 to-secondary/70 flex items-center justify-center shadow-lg shadow-secondary/20">
                        <MdEmail className="text-3xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-base-content">
                            Modelos de Email
                        </h1>
                        <p className="text-sm md:text-base text-base-content/60 mt-1">
                            {templates.length}{' '}
                            {templates.length === 1 ? 'modelo' : 'modelos'}
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button
                        className="btn btn-secondary gap-2"
                        onClick={openNewForm}
                    >
                        <MdAdd className="text-lg" />
                        Novo Modelo
                    </button>
                )}
            </div>

            {/* Lista de templates */}
            {templates.length === 0 ? (
                <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-2xl border border-base-300/50 shadow-lg p-16 text-center">
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-secondary/10 to-secondary/5 flex items-center justify-center">
                        <MdEmail className="text-5xl text-secondary/40" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-base-content mb-3">
                        Nenhum modelo criado
                    </h3>
                    <p className="text-base-content/60 mb-8 max-w-md mx-auto">
                        Crie modelos de email para agilizar o envio de
                        mensagens frequentes
                    </p>
                    {canCreate && (
                        <button
                            className="btn btn-secondary gap-2"
                            onClick={openNewForm}
                        >
                            <MdAdd className="text-lg" />
                            Criar Modelo
                        </button>
                    )}
                </div>
            ) : (
                <div className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm overflow-hidden">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Assunto</th>
                                <th>Criado em</th>
                                <th className="w-24"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {templates.map(tpl => (
                                <tr
                                    key={tpl.id}
                                    className="hover:bg-base-200/30"
                                >
                                    <td className="font-medium">
                                        {tpl.name}
                                    </td>
                                    <td className="text-base-content/60 text-sm">
                                        {tpl.subject || '-'}
                                    </td>
                                    <td className="text-base-content/50 text-sm">
                                        {format(
                                            new Date(tpl.createdAt),
                                            'dd/MM/yyyy',
                                            { locale: ptBR },
                                        )}
                                    </td>
                                    <td>
                                        <div className="flex items-center gap-1 justify-end">
                                            {canUpdate && (
                                                <button
                                                    className="btn btn-ghost btn-xs btn-square"
                                                    onClick={() =>
                                                        openEditForm(tpl)
                                                    }
                                                    title="Editar"
                                                >
                                                    <MdEdit className="text-base" />
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button
                                                    className="btn btn-ghost btn-xs btn-square text-error"
                                                    onClick={() =>
                                                        setDeleteModalId(
                                                            tpl.id,
                                                        )
                                                    }
                                                    title="Excluir"
                                                >
                                                    <MdDelete className="text-base" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Form Modal */}
            {showForm && (
                <dialog className="modal modal-open z-50">
                    <ModalContainer size="md">
                        <div className="flex items-center justify-between border-b border-base-200 pb-3 mb-4">
                            <h3 className="text-lg font-semibold">
                                {editingId
                                    ? 'Editar Modelo'
                                    : 'Novo Modelo'}
                            </h3>
                        </div>

                        <div className="flex flex-col gap-4 overflow-y-auto max-h-[65vh]">
                            <div className="form-control w-full">
                                <label className="label py-1">
                                    <span className="label-text text-xs font-semibold">
                                        Nome do modelo
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    value={formName}
                                    onChange={e =>
                                        setFormName(e.target.value)
                                    }
                                    placeholder="Ex: Boas-vindas"
                                    className="input input-bordered input-sm w-full"
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label py-1">
                                    <span className="label-text text-xs font-semibold">
                                        Assunto (opcional)
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    value={formSubject}
                                    onChange={e =>
                                        setFormSubject(e.target.value)
                                    }
                                    placeholder="Assunto padrão do modelo"
                                    className="input input-bordered input-sm w-full"
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label py-1">
                                    <span className="label-text text-xs font-semibold">
                                        Conteúdo
                                    </span>
                                </label>
                                <RichTextEditor
                                    content={formBodyHtml}
                                    onChange={setFormBodyHtml}
                                    placeholder="Digite o conteúdo do modelo..."
                                    minHeight="300px"
                                />
                            </div>
                        </div>

                        <ModalActions>
                            <button
                                className="btn btn-ghost btn-sm"
                                onClick={closeForm}
                                disabled={saving}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Salvando...' : 'Salvar'}
                            </button>
                        </ModalActions>
                    </ModalContainer>
                    <form method="dialog" className="modal-backdrop">
                        <button onClick={closeForm}>close</button>
                    </form>
                </dialog>
            )}

            <BackdropLoading visible={saving} />

            <ConfirmModal
                isOpen={!!deleteModalId}
                onClose={() => setDeleteModalId(null)}
                onConfirm={handleDelete}
                title="Excluir Modelo"
                message="Tem certeza que deseja excluir este modelo de email? Esta ação não pode ser desfeita."
                confirmButtonText="Excluir"
                loading={deleting}
            />
        </div>
    )
}
